import { SanitizeLevel, tracker } from '@openreplay/tracker';
import * as Sentry from '@sentry/react';

import { APP_SETTINGS, envInfo } from '@constants/constants';
import { AppEnvironment } from '@customTypes/app';

// Constants
const METADATA_SETUP_DELAY_MS = 150;
const METADATA_SETUP_RETRY_DELAY_MS = 100;
const METADATA_SETUP_MAX_RETRIES = 3;
const isLocalhost = (): boolean => APP_SETTINGS.environment === AppEnvironment.LOCAL;

// Environment-based logging
const isNonProd = APP_SETTINGS.environment !== AppEnvironment.PROD;
// Use function wrapper so it always calls the current console.log (works with test spies)
const log = isNonProd ? (...args: Parameters<typeof console.log>) => console.log(...args) : () => {};
const warn = (...args: Parameters<typeof console.warn>) => console.warn(...args); // Keep warnings in all environments

/**
 * Base sanitization configuration shared across all environments
 * Ensures consistent sanitization behavior in prod, staging, dev, and localhost
 * Data attributes can be added:
 * 1. Manually in components (data-openreplay-sanitize, data-openreplay-hidden)
 * 2. Automatically via form schemas (ui:openreplay metadata → templates add attributes)
 */
const getBaseSanitizationConfig = () => ({
  respectDoNotTrack: true,
  defaultInputMode: 0 as const, // 0 = masked, 1 = plain - masks all input fields by default
  // Mask sensitive data in input fields (more likely to contain PII)
  obscureInputNumbers: true, // Mask numbers in input fields
  obscureInputEmails: true, // Mask emails in input fields
  // Custom DOM sanitizer to mask sensitive display elements
  // Uses explicit data attributes for precise control (avoids over-obscuring non-sensitive data)
  domSanitizer: (node: Element): SanitizeLevel => {
    // Check for data attribute to completely hide elements
    if (node.hasAttribute('data-openreplay-hidden')) {
      return SanitizeLevel.Hidden;
    }
    // Check for data attribute to obscure/mask elements
    if (node.hasAttribute('data-openreplay-sanitize')) {
      return SanitizeLevel.Obscured;
    }
    return SanitizeLevel.Plain;
  },
});

type Environment = 'production' | 'localhost';

/**
 * Set up OpenReplay metadata after session starts
 */
async function setupOpenReplayMetadata(environment: Environment): Promise<void> {
  try {
    tracker.setMetadata('environment', envInfo.keycloak_realm);
    const modeSuffix = isLocalhost() ? ' (localhost mode)' : '';
    log(`OpenReplay metadata set${modeSuffix}`);

    // Log session info if available (localhost only)
    if (isLocalhost() && tracker.getSessionInfo) {
      try {
        const sessionInfo = tracker.getSessionInfo();
        log('OpenReplay session info (localhost mode):', sessionInfo);
      } catch (e) {
        warn('OpenReplay session info not available (localhost mode):', e);
        Sentry.captureException(e, {
          tags: { component: 'OpenReplay', action: 'getSessionInfo', environment: 'localhost' },
          level: 'warning',
        });
      }
    }
  } catch (e) {
    const errorMessage = `Failed to set OpenReplay metadata${isLocalhost() ? ' (localhost mode)' : ''}`;
    warn(errorMessage, e);
    Sentry.captureException(e, {
      tags: { component: 'OpenReplay', action: 'setMetadata', environment },
      level: 'warning',
    });
  }
}

/**
 * Set up pagehide event listener to stop tracker on page unload
 */
let pageHideHandler: ((event: PageTransitionEvent) => void) | null = null;

function setupPageHideListener(): void {
  // Prevent duplicate listeners
  if (pageHideHandler) return;

  pageHideHandler = (event: PageTransitionEvent) => {
    const currentEnvironment: Environment = isLocalhost() ? 'localhost' : 'production';

    // persisted = false means it's a real unload (not back/forward cache)
    if (!event.persisted) {
      try {
        tracker.stop();
        const stopInfo: { timestamp: string; reason: string; persisted: boolean; mode?: string } = {
          timestamp: new Date().toISOString(),
          reason: 'pagehide',
          persisted: false,
        };
        if (isLocalhost()) {
          stopInfo.mode = 'localhost';
        }
        const modeSuffix = isLocalhost() ? ' (localhost mode)' : '';
        log(`OpenReplay session stopped on page unload${modeSuffix}:`, stopInfo);

        // Store in localStorage for debugging
        try {
          localStorage.setItem('openreplay_last_stop', JSON.stringify(stopInfo));
        } catch {
          // Ignore localStorage errors
        }
      } catch (e) {
        const errorMessage = `Error stopping OpenReplay session on unload${isLocalhost() ? ' (localhost mode)' : ''}`;

        warn(errorMessage, e);
        Sentry.captureException(e, {
          tags: { component: 'OpenReplay', action: 'stop', environment: currentEnvironment },
          level: 'warning',
        });
      }
    } else {
      const modeSuffix = isLocalhost() ? ' (localhost mode)' : '';
      log(`Page cached (back/forward) - OpenReplay session continues${modeSuffix}`);
    }
  };

  window.addEventListener('pagehide', pageHideHandler);
}

/**
 * Start OpenReplay tracker and set up metadata
 */
function startOpenReplayTracker(environment: Environment, extraStartConfig?: Record<string, unknown>): void {
  const modeSuffix = isLocalhost() ? ' (localhost mode)' : '';
  const startMessage = `OpenReplay session started successfully${modeSuffix}`;
  const errorMessage = isLocalhost() ? 'OpenReplay start failed (localhost mode)' : 'OpenReplay start failed';

  tracker
    .start()
    .then(() => {
      log(startMessage);
      // Wait for session to be ready with retry mechanism
      const setupMetadataWithRetry = (attempts = 0) => {
        // Check if getSessionInfo is available and callable, or if we've reached max retries
        const sessionInfoAvailable = typeof tracker.getSessionInfo === 'function';
        if (sessionInfoAvailable || attempts >= METADATA_SETUP_MAX_RETRIES) {
          setupOpenReplayMetadata(environment);
        } else {
          setTimeout(() => setupMetadataWithRetry(attempts + 1), METADATA_SETUP_RETRY_DELAY_MS);
        }
      };
      setTimeout(() => setupMetadataWithRetry(), METADATA_SETUP_DELAY_MS);
    })
    .catch(error => {
      warn(errorMessage, error);
      Sentry.captureException(error, {
        tags: { component: 'OpenReplay', action: 'start', environment },
        level: 'error',
        extra: {
          projectKey: envInfo.openreplay_project_key ? 'configured' : 'missing',
          ingestPoint: envInfo.openreplay_ingest_point,
          ...extraStartConfig,
        },
      });
    });
}

/**
 * Initialize OpenReplay tracker
 * Should be called once at application startup, before React renders
 */
export function initializeOpenReplay(): void {
  // Skip when running inside Cypress to avoid extra traffic and flakiness
  if (typeof window !== 'undefined' && (window as Window & { Cypress?: unknown }).Cypress) {
    log('OpenReplay not initialized: Cypress detected');
    return;
  }

  // Check if DoNotTrack is enabled in the user's browser
  const isDoNotTrackEnabled = navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes';

  try {
    if (!envInfo.openreplay_project_key) {
      log('OpenReplay not initialized: project key not configured');
      return;
    }

    if (isDoNotTrackEnabled) {
      log('OpenReplay not initialized: DoNotTrack is enabled');
      return;
    }

    const environment: Environment = isLocalhost() ? 'localhost' : 'production';

    if (isLocalhost()) {
      log('Initializing OpenReplay tracker in local development mode');
    }

    // Configure tracker with base config + environment-specific options
    const config = {
      ...getBaseSanitizationConfig(),
      // Always apply localhost-specific options when running on localhost
      // (needed for HTTP recording, regardless of which environment config is being tested)
      ...(isLocalhost() && {
        inlineCss: 3, // Required for HTTP localhost - captures CSS inline
        __DISABLE_SECURE_MODE: true, // Allow HTTP recording (localhost only)
      }),
      // Critical config values at the end to prevent accidental overwriting
      projectKey: envInfo.openreplay_project_key,
      ingestPoint: envInfo.openreplay_ingest_point,
    };

    // Debug logging for localhost to verify config is applied correctly
    if (isLocalhost()) {
      log('OpenReplay localhost config:', {
        inlineCss: config.inlineCss,
        __DISABLE_SECURE_MODE: config.__DISABLE_SECURE_MODE,
        projectKey: config.projectKey ? 'configured' : 'missing',
        ingestPoint: config.ingestPoint,
      });
    }

    tracker.configure(config);

    // Start tracker and set up listeners
    startOpenReplayTracker(environment, isLocalhost() ? { inlineCss: 3 } : undefined);
    setupPageHideListener();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'OpenReplay', action: 'initialize' },
      level: 'error',
      extra: {
        isLocalhost: isLocalhost(),
        doNotTrackEnabled: isDoNotTrackEnabled,
        hasProjectKey: !!envInfo.openreplay_project_key,
      },
    });
  }
}

/**
 * Identify a user in OpenReplay
 * @param userId - The user's unique identifier
 * @param name - Optional user's full name
 * @param email - Optional user's email address
 */
export function identifyUser(userId: string, name?: string, email?: string): void {
  tracker.setUserID(userId);
  if (name) {
    tracker.setMetadata('name', name);
  }
  if (email) {
    tracker.setMetadata('email', email);
  }
  tracker.setMetadata('auth_status', 'authenticated');
}

/**
 * Clear user identification in OpenReplay (call on logout)
 */
export function clearUser(): void {
  // @ts-ignore - setUserID accepts null at runtime to clear user ID
  tracker.setUserID(null);
  tracker.setMetadata('auth_status', 'unauthenticated');
  tracker.setMetadata('name', '');
  tracker.setMetadata('email', '');
}

/**
 * Set additional metadata for the current session
 * @param key - Metadata key
 * @param value - Metadata value
 */
export function setMetadata(key: string, value: string): void {
  tracker.setMetadata(key, value);
}

// Export the tracker instance for advanced usage if needed
export { tracker };
